import { Link } from 'react-router-dom';
import Icon, { type IconName } from '@/components/Icon';

// ============================================================
// Stocora — Settings hub (2026-07-07)
// ============================================================
// Landing screen for clinic admin surfaces, reached from the
// Header account menu. Today it holds a single row (Vendors);
// built as a list so future admin surfaces (clinic profile,
// members, ship-to, notification prefs...) drop in as rows
// without restructuring. Reached via /settings; each row links
// to its own route.
//
// No permission gate on the HUB itself — every role can open
// Settings. Individual sub-pages gate their own edit affordances
// (e.g. Vendors is read-only for staff). This keeps the hub a
// stable, always-reachable entry point.
// ============================================================

interface SettingsRow {
  to: string;
  icon: IconName;
  label: string;
  description: string;
}

const SETTINGS_ROWS: readonly SettingsRow[] = [
  {
    to: '/settings/profile',
    icon: 'user',
    label: 'Practice Profile',
    description: 'Purchasing contact and audit thresholds',
  },
  {
    to: '/settings/vendors',
    icon: 'mail',
    label: 'Vendors',
    description: 'Order and sales-rep email addresses',
  },
  {
    to: '/settings/team',
    icon: 'user',
    label: 'Team',
    description: 'Invite teammates and manage roles',
  },
] as const;

export default function SettingsPage() {
  return (
    <div className="p-4 space-y-3">
      <h1 className="text-base font-semibold text-slate-900">Settings</h1>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
        {SETTINGS_ROWS.map((row) => (
          <Link
            key={row.to}
            to={row.to}
            className="flex items-center gap-3 px-3.5 py-3 hover:bg-slate-50 active:bg-slate-100 transition"
          >
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <Icon name={row.icon} className="w-4.5 h-4.5 text-slate-600" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium text-slate-900">{row.label}</div>
              <div className="text-[12px] text-slate-500 truncate">
                {row.description}
              </div>
            </div>
            <Icon
              name="chevright"
              className="w-4 h-4 text-slate-300 shrink-0"
              strokeWidth={2}
            />
          </Link>
        ))}
      </div>
    </div>
  );
}
